import React, { useRef } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  ExpressCheckoutElement,
} from "@stripe/react-stripe-js";
import type { StripeExpressCheckoutElementOptions } from "@stripe/stripe-js";
import api from "../api";

export type Track = { name: string; artists: { name: string }[] };

type Props = {
  orderId: string | null;
  emailForLink: string;
  setEmailForLink: (v: string) => void;
  showCardForm: boolean;
  setShowCardForm: (v: boolean) => void;
  processing: boolean;
  setProcessing: (v: boolean) => void;
  payError: string | null;
  setPayError: (v: string | null) => void;
  selectedTrack: Track | null;
  totalToCharge: number;
  onPaymentSuccess?: (piId?: string) => void | Promise<void>;
  canDoubleBid?: boolean;
  onDoubleBid?: () => void;
};

const InlinePayOptions: React.FC<Props> = ({
  orderId,
  showCardForm,
  setShowCardForm,
  processing,
  setProcessing,
  payError,
  setPayError,
  onPaymentSuccess,
  canDoubleBid,
  onDoubleBid,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const successUrl =
    `${window.location.origin}/success` +
    (orderId ? `?order_id=${encodeURIComponent(orderId)}` : "");

  const expressOptions: StripeExpressCheckoutElementOptions = {
    paymentMethodOrder: ["apple_pay", "google_pay", "link", "card"],
    buttonType: { applePay: "plain", googlePay: "buy" },
    layout: { maxColumns: 1 } as any,
  };

  const confirmingRef = useRef(false);

  const confirmOnServer = async (piId?: string) => {
    if (!orderId) return;
    try {
      await api.post("/payments/confirm-intent", { orderId, piId });
    } catch (e) {
      console.error("confirm-intent error:", e);
    }
  };

  const runConfirm = async (returnUrl: string) => {
    if (!stripe || !elements) return { ok: false, msg: "Stripe not ready" };
    if (confirmingRef.current) return { ok: false, msg: "Already confirming" };
    confirmingRef.current = true;
    setProcessing(true);
    setPayError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      confirmingRef.current = false;
      setProcessing(false);
      return { ok: false, msg: submitError.message || "Payment error" };
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (!error) {
      return { ok: true, piId: paymentIntent?.id };
    }

    if ((error as any)?.code === "payment_intent_unexpected_state") {
      const piId =
        (error as any)?.payment_intent?.id ||
        (error as any)?.payment_intent_client_secret?.split("_secret")[0];
      return { ok: true, piId };
    }

    confirmingRef.current = false;
    setProcessing(false);
    setPayError(error.message || "Payment error");
    return { ok: false, msg: error.message || "Payment error" };
  };

  const handleExpressConfirm = async (event: any) => {
    if (!stripe || !elements) {
      event.complete && event.complete("fail");
      return;
    }

    const res = await runConfirm(successUrl);
    if (!res.ok) {
      event.complete && event.complete("fail");
      return;
    }

    await confirmOnServer(res.piId);

    event.complete && event.complete("success");
    confirmingRef.current = false;
    setProcessing(false);

    if (onPaymentSuccess) await onPaymentSuccess(res.piId);
    else if (orderId)
      window.location.href = `${window.location.origin}/success?order_id=${encodeURIComponent(
        orderId
      )}`;
  };

  const confirmCard = async () => {
    const res = await runConfirm(successUrl);
    if (!res.ok) return;

    await confirmOnServer(res.piId);

    confirmingRef.current = false;
    setProcessing(false);
    await onPaymentSuccess?.(res.piId);
  };

  return (
    <div className="rq-inline-pay">
      <div className="rq-wallets">
        {canDoubleBid && onDoubleBid && (
          <button
            type="button"
            className="rq-double-bid-btn"
            onClick={onDoubleBid}
            disabled={processing}
          >
            ⚡ Double your bid
          </button>
        )}

        <div className="rq-express-vertical">
          <ExpressCheckoutElement
            options={expressOptions}
            onConfirm={handleExpressConfirm}
            onReady={(ev: any) =>
              console.log("availablePaymentMethods:", ev?.availablePaymentMethods)
            }
          />
        </div>

        {!showCardForm ? (
          <button className="rq-add-card" onClick={() => setShowCardForm(true)}>
            Add card
          </button>
        ) : (
          <div className="rq-card-form">
            <PaymentElement />
            <button
              className="rq-pay-now"
              onClick={confirmCard}
              disabled={processing || !stripe || !elements}
            >
              {processing ? "Processing…" : "Pay now"}
            </button>
          </div>
        )}

        {payError && <div className="rq-pay-error">{payError}</div>}
      </div>
    </div>
  );
};

export default InlinePayOptions;
